import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Button from '../ui/Button';
import { Dialog } from './accounts/AccountBits';
import nurseryService from '../../services/nursery';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { LOT_STAGE_LABELS, MANUAL_LOT_STAGES } from '../../types/nursery';
import type { LotChangeResult, LotStage, NurseryLot } from '../../types/nursery';

export type LotChangeMode = 'stage' | 'archive' | 'restore';

type LotLike = Pick<NurseryLot, 'id' | 'cultivar_name' | 'rootstock_name' | 'current_stage' | 'status' | 'current_count'>
  & { lot_code?: string | null };

function stageLabel(stage: string, t: TFunction): string {
  return t(`stages.${stage}`, { defaultValue: LOT_STAGE_LABELS[stage as LotStage] ?? stage });
}

function lotName(l: LotLike, t: TFunction): string {
  return `${l.cultivar_name ?? t('lotFallback')}${l.rootstock_name ? ` / ${l.rootstock_name}` : ''}${l.lot_code ? ` (${l.lot_code})` : ` #${l.id}`}`;
}

/**
 * Change the stage of one lot or many, or take them out of stock and back.
 *
 * Free stock counts only ready lots, so "Ready to sell" is what puts trees on
 * the order sheet. Every change is recorded on the lot's history. The server
 * changes the lots it can and says why it left the rest; this shows that,
 * rather than closing as though everything moved.
 */
export function LotStageDialog({ lots, mode, initialStage, onClose, onDone }: {
  lots: LotLike[];
  mode: LotChangeMode;
  initialStage?: LotStage;
  onClose: () => void;
  /** Called once something changed, before the dialog closes. */
  onDone: (result: LotChangeResult) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'LotStageDialog' });
  const tz = useOrgTimezone();
  const stages = new Set(lots.map(l => l.current_stage));
  const sharedStage = stages.size === 1 ? lots[0]?.current_stage : null;

  const [stage, setStage] = useState<LotStage | ''>(initialStage ?? '');
  const [date, setDate] = useState(getTodayInTz(tz));
  const [readyDate, setReadyDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LotChangeResult | null>(null);

  const trees = lots.reduce((n, l) => n + (l.current_count || 0), 0);
  const count = t('lots', { count: lots.length });
  const subtitle = lots.length === 1
    ? t('subtitle', { name: lotName(lots[0], t), trees: trees.toLocaleString() })
    : t('subtitle', { name: count, trees: trees.toLocaleString() });
  const leavingReady = stage !== '' && stage !== 'ready' && lots.some(l => l.current_stage === 'ready');

  const title = mode === 'archive' ? t('titleArchive')
    : mode === 'restore' ? t('titleRestore')
    : t('titleStage');

  const submit = async () => {
    if (mode === 'stage' && !stage) { setError(t('chooseStage')); return; }
    setBusy(true);
    setError(null);
    try {
      const ids = lots.map(l => l.id);
      const res = mode === 'stage'
        ? await nurseryService.changeLotStage({
          lot_ids: ids, stage: stage as LotStage, date,
          ready_date: stage !== 'ready' && readyDate ? readyDate : undefined,
          notes: notes || null,
        })
        : await nurseryService.changeLotStatus({ lot_ids: ids, action: mode, date, notes: notes || null });
      if (!res.success) { setError(errorMessage(res, t('nothingChangedError'))); setBusy(false); return; }
      if (res.data.changed.length > 0) onDone(res.data);
      if (res.data.skipped.length === 0) { onClose(); return; }
      setResult(res.data);
    } catch (e) {
      setError(errorMessage(e, t('nothingChangedError')));
    }
    setBusy(false);
  };

  if (result) {
    const byId = new Map(lots.map(l => [l.id, l]));
    return (
      <Dialog title={title} subtitle={subtitle} onClose={onClose}
        footer={<Button onClick={onClose}>{t('done')}</Button>}>
        {result.changed.length > 0 && (
          <p className="text-sm text-green-700">
            {t('changedOf', { changed: result.changed.length, count: lots.length })}
          </p>
        )}
        <p className="text-sm text-neutral-700">
          {result.skipped.length === lots.length ? t('nothingChanged') : t('leftAsTheyWere')}
        </p>
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
          {result.skipped.map(s => (
            <li key={s.id} className="px-3 py-2 text-sm">
              <div className="font-medium text-neutral-800 break-words">{byId.get(s.id) ? lotName(byId.get(s.id)!, t) : t('lotNumber', { id: s.id })}</div>
              <div className="text-neutral-500 break-words">{s.reason}</div>
            </li>
          ))}
        </ul>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={submit} disabled={busy || (mode === 'stage' && !stage)}
            variant={mode === 'archive' ? 'danger' : 'primary'}>
            {busy ? t('saving')
              : mode === 'archive' ? t('takeOut', { lots: count })
              : mode === 'restore' ? t('putBack', { lots: count })
              : stage ? t('moveTo', { lots: count, stage: stageLabel(stage, t).toLowerCase() }) : t('chooseStage')}
          </Button>
        </>
      }
    >
      {mode === 'stage' && (
        <>
          <div>
            <div className="text-sm font-medium text-neutral-700 mb-2">{t('moveToHeading')}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label={t('stage')}>
              {MANUAL_LOT_STAGES.map(s => {
                const isNow = sharedStage === s;
                const on = stage === s;
                return (
                  <button key={s} type="button" role="radio" aria-checked={on} disabled={isNow}
                    onClick={() => setStage(s)}
                    className={cn(
                      'rounded-lg border px-2 py-2 text-sm text-left min-w-0',
                      s === 'ready' && 'col-span-2 sm:col-span-4 font-medium',
                      on ? (s === 'ready' ? 'border-green-600 bg-green-50 text-green-800 ring-1 ring-green-600'
                        : 'border-primary-600 bg-primary-50 text-primary-800 ring-1 ring-primary-600')
                        : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50',
                      isNow && 'opacity-50 cursor-not-allowed',
                    )}>
                    <span className="break-words">{stageLabel(s, t)}</span>
                    {isNow && <span className="block text-xs text-neutral-500">{t('now')}</span>}
                  </button>
                );
              })}
            </div>
            {!sharedStage && lots.length > 1 && (
              <p className="text-xs text-neutral-500 mt-1">{t('differentStages', { list: [...stages].map(s => stageLabel(s, t)).join(', ') })}</p>
            )}
          </div>

          {stage === 'ready' && (
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 break-words">
              {t('readyNote')}
            </p>
          )}

          {stage !== '' && stage !== 'ready' && (
            <div>
              <label htmlFor="lot-stage-ready" className="block text-sm font-medium text-neutral-700 mb-1">{t('expectedReady')}</label>
              <input id="lot-stage-ready" type="date" value={readyDate} onChange={e => setReadyDate(e.target.value)}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
              <p className="text-xs text-neutral-500 mt-1 break-words">
                {leavingReady
                  ? t('readyBlankLeaving')
                  : t('readyBlankKeep')}
              </p>
            </div>
          )}
        </>
      )}

      {mode === 'archive' && (
        <p className="text-sm text-neutral-700 break-words">
          {t('archiveNote', { trees: trees.toLocaleString() })}
        </p>
      )}
      {mode === 'restore' && (
        <p className="text-sm text-neutral-700 break-words">
          {t('restoreNote')}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="lot-stage-date" className="block text-sm font-medium text-neutral-700 mb-1">{t('date')}</label>
          <input id="lot-stage-date" type="date" value={date} onChange={e => setDate(e.target.value)}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label htmlFor="lot-stage-notes" className="block text-sm font-medium text-neutral-700 mb-1">
            {mode === 'archive' ? t('reason') : t('notes')}
          </label>
          <input id="lot-stage-notes" value={notes} onChange={e => setNotes(e.target.value)}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
    </Dialog>
  );
}

export default LotStageDialog;
